import { useCallback, useEffect, useMemo, useState } from 'react';
import { useScopeGitUiStore } from '../store/scopeGitUiStore';
import { useTeamsStore } from '../store/teamsStore';
import { useAuthStore } from '../store/authStore';
import { useGitSyncStore } from '../store/gitSyncStore';
import { setGitSecrets } from '../store/uiModalStore';
import { useNotificationStore } from '../store/notificationStore';
import { personalScopeId } from '../services/personalScope';
import { getAllScopeGitLinks } from '../services/accountsStore';
import { connectableScopes } from '../services/git/scopeRepo';
import { connectScope, pushScope, pullScope, scopeStatus, resolveScopeConflicts, abortScopeMerge, removeTeamRepo } from '../services/git/scopeGitSync';
import { teamIdOf } from '../services/git/teamRepo';
import teamsService from '../services/teamsService';
import useScopeGitLocal from './useScopeGitLocal';
import i18n from '../i18n';

const notify = () => useNotificationStore.getState();
const message = (err) => err?.message || String(err);

/**
 * What a pull (or a merge on sync) did, in a sentence: "Pulled: 2 documents
 * changed, 1 task changed, 1 project changed." — from every contributor's result.
 */
export function pulledWords(applied) {
  const d = applied?.documents || {};
  const b = applied?.boards || {};
  const p = applied?.projects || {};
  const n = (count, one, many) => (count ? `${count} ${count === 1 ? one : many}` : null);
  const parts = [
    n(d.created, 'new page', 'new pages'),
    n(d.bodies, 'page changed', 'pages changed'),
    n(d.moved, 'page moved or renamed', 'pages moved or renamed'),
    n(d.deleted, 'page deleted', 'pages deleted'),
    n((b.boardsCreated || 0) + (b.boardsChanged || 0), 'board changed', 'boards changed'),
    n(b.tasksChanged, 'task changed', 'tasks changed'),
    n(b.tasksDeleted, 'task deleted', 'tasks deleted'),
    n((p.projectsCreated || 0) + (p.projectsChanged || 0), 'project changed', 'projects changed'),
    n((p.linksAdded || 0) + (p.linksRemoved || 0), 'link changed', 'links changed'),
    n(p.projectsDeleted, 'project deleted', 'projects deleted'),
    n((d.uploads || 0) + (b.uploads || 0), 'new image uploading', 'new images uploading'),
  ].filter(Boolean);
  return parts.length ? `Pulled: ${parts.join(', ')}.` : 'Pulled the latest changes.';
}

/**
 * A merge that could not all be applied was undone (scopeGitSync): nothing from
 * it is kept — the remote stays "behind" and the next pull tries again.
 */
function reportNotApplied(scope, result) {
  const problems = result.problems || [];
  useGitSyncStore.getState().recordError?.(String(scope.key), `${problems.length} file(s) could not be applied — nothing from the repository was taken in; pull again`);
  notify().error(
    `${problems.map((p) => `${p.file}: ${p.error}`).join(' · ')} — nothing from this pull was kept, so nothing is lost: fix it (or finish your edit) and pull again.`,
    `${scope.name}: not pulled`
  );
}

/** Held-back deletes and files that could not be applied, across every contributor. */
function pullWarnings(applied) {
  const all = Object.values(applied || {});
  return {
    held: all.reduce((sum, r) => sum + (r?.heldBackDeletes || 0), 0),
    problems: all.flatMap((r) => r?.problems || []),
  };
}

/**
 * The Git page's "Boards & documents": your scope and each team's, their link,
 * status and the three actions — connect, sync, pull (git sync Stage 2, S2.3).
 * Status lives in gitSyncStore under the scope key, so the same rows and
 * words as workspaces show it.
 */
// Module-level, so they are stable and never a hook dependency.
const setBusyKey = (key) => useScopeGitUiStore.getState().setBusyKey(key);
const setConflict = (value) => useScopeGitUiStore.getState().setConflict(value);

export default function useScopeGit() {
  const teams = useTeamsStore((s) => s.teams);
  const signedIn = useAuthStore((s) => s.isAuthenticated);
  const [links, setLinks] = useState({});
  // Outlive the Git page: a sync started there goes on when you switch tabs (scopeGitUiStore).
  const busyKey = useScopeGitUiStore((s) => s.busyKey);
  const conflict = useScopeGitUiStore((s) => s.conflict);

  // The repository stored on each team (card 84628c27), read fresh on the Git page: id → { url, provider } | null.
  const [teamRepos, setTeamRepos] = useState({});

  const teamIds = signedIn ? (teams || []).map((t) => String(t.id)).join(',') : '';
  const reloadTeamRepos = useCallback(async () => {
    const ids = teamIds ? teamIds.split(',') : [];
    const rows = await Promise.all(ids.map(async (id) => [id, await teamsService.getTeamRepo(id).catch(() => undefined)]));
    setTeamRepos(Object.fromEntries(rows.filter(([, repo]) => repo !== undefined)));
  }, [teamIds]);
  useEffect(() => {
    reloadTeamRepos();
  }, [reloadTeamRepos]);

  const reloadLinks = useCallback(async () => {
    const next = await getAllScopeGitLinks().catch(() => ({}));
    setLinks(next);
    useGitSyncStore.getState().setLinkedScopes(next); // a disconnected scope stops counting for the rail dot
  }, []);
  useEffect(() => {
    reloadLinks();
  }, [reloadLinks, signedIn]);

  const scopes = useMemo(
    () =>
      connectableScopes({ personalKey: personalScopeId(), teams: signedIn ? teams : [] }).map((s) => {
        const teamId = teamIdOf(s.key);
        const fresh = teamId && teamId in teamRepos ? { teamRepo: teamRepos[teamId] } : {};
        return { ...s, ...fresh, id: s.key, git: links[s.key] || null };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- personalScopeId follows signedIn
    [teams, links, signedIn, teamRepos]
  );

  const refreshStatus = useCallback(async (scope) => {
    if (!scope?.git) return;
    try {
      const status = await scopeStatus(scope);
      useGitSyncStore.getState().markScope(String(scope.key), { local: scope.git.provider === 'local' });
      useGitSyncStore.getState().setStatus(String(scope.key), status);
    } catch {
      /* the row keeps its last state */
    }
  }, []);

  /** One result → the right words (and the secret-scan dialog when it found something). */
  const handleResult = useCallback(
    async (scope, result, retry) => {
      const store = useGitSyncStore.getState();
      if (result.status === 'secrets') {
        setGitSecrets({ workspace: { id: scope.key, name: scope.name }, findings: result.findings, commitAnyway: retry });
        return;
      }
      if (result.status === 'conflicts') {
        // Both sides changed the same files: keep mine / keep theirs per file (closing cancels the merge).
        setConflict({ scope, conflicts: result.conflicts });
        return;
      }
      if (result.status === 'not_applied') {
        reportNotApplied(scope, result);
        await refreshStatus(scope);
        return;
      }
      store.recordSuccess(String(scope.key));
      // "This device only" (T10): the first version, saved — nothing went anywhere.
      if (result.local) notify().success('Tracking on this device — the first version is saved.', scope.name);
      else notify().success(result.status === 'diverged' ? 'Pushed; the remote moved again meanwhile.' : 'Synced with the repository.', scope.name);
      await reloadLinks();
      await refreshStatus({ ...scope, git: result.git || scope.git });
    },
    [reloadLinks, refreshStatus]
  );

  const run = useCallback(async (scope, work) => {
    setBusyKey(scope.key);
    try {
      await work();
    } catch (err) {
      useGitSyncStore.getState().recordError?.(String(scope.key), message(err));
      notify().error(message(err), `${scope.name}: not synced`);
    } finally {
      setBusyKey(null);
    }
  }, []);

  /**
   * Connect from the connect dialog: errors are thrown back to it (it shows
   * them in place); the secret-scan's "Commit anyway" retries outside it.
   * @returns {Promise<boolean>} true when the dialog can close
   */
  const connect = useCallback(
    async (scope, opts) => {
      setBusyKey(scope.key);
      try {
        const result = await connectScope(scope, opts);
        await handleResult(scope, result, () => run(scope, () => connect(scope, { ...opts, allowSecrets: true })));
        const teamId = teamIdOf(scope.key);
        if (teamId && result.teamRepo !== undefined) setTeamRepos((prev) => ({ ...prev, [teamId]: result.teamRepo }));
        if (result.teamRepoError) notify().warning(i18n.t('gitTeamRepo.notSaved', { error: result.teamRepoError }), scope.name);
        return true;
      } finally {
        setBusyKey(null);
      }
    },
    [run, handleResult]
  );

  const sync = useCallback(
    (scope, opts = {}) =>
      run(scope, async () => {
        const result = await pushScope(scope, opts);
        await handleResult(scope, result, () => sync(scope, { allowSecrets: true }));
      }),
    [run, handleResult]
  );

  const pull = useCallback(
    (scope, opts = {}) =>
      run(scope, async () => {
        const result = await pullScope(scope, opts);
        if (result?.status === 'secrets' || result?.status === 'conflicts' || result?.status === 'not_applied') {
          await handleResult(scope, result, () => pull(scope, { allowSecrets: true }));
          return;
        }
        useGitSyncStore.getState().recordSuccess(String(scope.key));
        if (result?.applied) console.info('[git] pulled', scope.key, JSON.stringify(result.applied));
        const { held, problems } = pullWarnings(result?.applied);
        if (held) {
          notify().warning(`${held} items were deleted in the repository — more than one pull deletes at once, so they were kept. Delete them in Apiboo if that was meant.`, scope.name);
        }
        if (problems.length) notify().error(problems.map((p) => `${p.file}: ${p.error}`).join(' · '), `${scope.name}: ${problems.length} not applied`);
        const upToDate = result?.sent ? 'Up to date — your latest version was sent.' : 'Already up to date.';
        notify().success(result?.status === 'up_to_date' ? upToDate : pulledWords(result?.applied), scope.name);
        // The pull made a version it could not send: it waits for the next Sync now.
        if (result?.sendError) notify().warning(`Pulled, but your latest version was not sent: ${result.sendError}`, scope.name);
        await refreshStatus(scope);
      }),
    [run, handleResult, refreshStatus]
  );

  const resolveConflicts = useCallback(
    async (resolutions) => {
      const scope = conflict?.scope;
      if (!scope) return;
      setConflict(null);
      await run(scope, async () => {
        const result = await resolveScopeConflicts(scope, resolutions);
        if (result?.status === 'not_applied') {
          reportNotApplied(scope, result);
          return;
        }
        useGitSyncStore.getState().recordSuccess(String(scope.key));
        notify().success(pulledWords(result?.applied), scope.name);
        await refreshStatus(scope);
      });
    },
    [conflict, run, refreshStatus]
  );

  const cancelConflict = useCallback(async () => {
    const scope = conflict?.scope;
    setConflict(null);
    if (scope) await abortScopeMerge(scope.key).catch(() => {});
  }, [conflict]);

  /** "Remove the team's repository" (owners/admins): the team forgets it; every device keeps its own link. */
  const removeRepo = useCallback(
    (scope) =>
      run(scope, async () => {
        await removeTeamRepo(scope);
        notify().success(i18n.t('gitTeamRepo.removed'), scope.name);
        await reloadTeamRepos();
      }),
    [run, reloadTeamRepos]
  );

  // The personal scope tracked on this device only (T10): Save version · Add a remote · Stop tracking.
  const local = useScopeGitLocal({ setBusyKey, reloadLinks, refreshStatus, setConflict });

  return {
    scopes,
    busyKey,
    connect,
    sync,
    pull,
    refreshStatus,
    conflict,
    resolveConflicts,
    cancelConflict,
    removeRepo,
    saveVersion: local.saveVersion,
    addRemote: local.addRemote,
    stopTracking: local.stopTracking,
  };
}
